import React, { useState, useRef } from 'react';
import { Button } from './ui/button';

import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Upload, FileText, CheckCircle, AlertTriangle, Loader2, FileUp, Eye, X } from 'lucide-react';
import mammoth from 'mammoth';
import { toast } from 'sonner';
import { sanitizeHtml } from '../utils/sanitizeHtml';

export default function WordImporter({ 
  onImport, 
  buttonVariant = 'outline',
  buttonText = 'Importer Word'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertedHtml, setConvertedHtml] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Réinitialiser l'état
  const resetState = () => {
    setFile(null);
    setConvertedHtml('');
    setPreviewMode(false);
    setError('');
  };

  // Gérer la sélection de fichier
  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Vérifier le type de fichier
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    
    if (!validTypes.includes(selectedFile.type) && 
        !selectedFile.name.endsWith('.docx') && 
        !selectedFile.name.endsWith('.doc')) {
      setError('Format non supporté. Veuillez sélectionner un fichier .docx ou .doc');
      return;
    }

    if (selectedFile.name.endsWith('.doc')) {
      setError('Les fichiers .doc (ancien format) ne sont pas supportés. Veuillez convertir en .docx');
      return;
    }

    setFile(selectedFile);
    setError('');
    
    // Convertir automatiquement
    await convertToHtml(selectedFile);
  };

  // Convertir Word en HTML
  const convertToHtml = async (wordFile) => {
    setConverting(true);
    setError('');

    try {
      const arrayBuffer = await wordFile.arrayBuffer();
      
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Title'] => h1:fresh",
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "p[style-name='Titre'] => h1:fresh",
            "p[style-name='Titre 1'] => h1:fresh",
            "p[style-name='Titre 2'] => h2:fresh",
            "p[style-name='Titre 3'] => h3:fresh",
            "b => strong",
            "i => em",
            "u => u",
            "strike => s"
          ]
        }
      );

      // Nettoyer et améliorer le HTML
      let html = result.value;
      
      // Ajouter des classes pour le style
      html = html
        .replace(/<h1>/g, '<h1 class="text-2xl font-bold mb-4 text-slate-900">')
        .replace(/<h2>/g, '<h2 class="text-xl font-bold mb-3 mt-6 text-slate-800">')
        .replace(/<h3>/g, '<h3 class="text-lg font-semibold mb-2 mt-4 text-slate-700">')
        .replace(/<p>/g, '<p class="mb-3 text-slate-700">')
        .replace(/<ul>/g, '<ul class="list-disc ml-6 mb-4">')
        .replace(/<ol>/g, '<ol class="list-decimal ml-6 mb-4">')
        .replace(/<li>/g, '<li class="mb-1">')
        .replace(/<table>/g, '<table class="w-full border-collapse border border-slate-300 mb-4">')
        .replace(/<th>/g, '<th class="border border-slate-300 bg-slate-100 p-2 text-left font-semibold">')
        .replace(/<td>/g, '<td class="border border-slate-300 p-2">');

      setConvertedHtml(html);

      // Afficher les avertissements si présents
      if (result.messages && result.messages.length > 0) {
        const warnings = result.messages
          .filter(m => m.type === 'warning')
          .map(m => m.message)
          .join(', ');
        if (warnings) {
          toast.warning(`Avertissements de conversion: ${warnings}`);
        }
      }

      toast.success('Document converti avec succès !');
    } catch (err) {
      setError(`Erreur lors de la conversion: ${err.message}`);
      toast.error('Erreur lors de la conversion du document');
    } finally {
      setConverting(false);
    }
  };

  // Valider et importer
  const handleImport = () => {
    if (!convertedHtml) {
      setError('Aucun contenu à importer');
      return;
    }

    if (onImport) {
      onImport(convertedHtml, file?.name || 'document.docx');
    }
    
    toast.success('Modèle importé avec succès !');
    setIsOpen(false);
    resetState();
  };

  // Fermer le dialog
  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        onClick={() => setIsOpen(true)}
        className="border-purple-600 text-purple-600 hover:bg-purple-50"
      >
        <Upload className="h-4 w-4 mr-2" />
        {buttonText}
      </Button>

      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5 text-purple-600" />
              Importer un modèle Word
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Zone de sélection de fichier */}
            <div 
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                ${file ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-purple-400 hover:bg-purple-50'}
              `}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.doc"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {file ? (
                <div className="flex flex-col items-center gap-3">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">{file.name}</p>
                    <p className="text-sm text-green-600">
                      {(file.size / 1024).toFixed(1)} Ko
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetState();
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Changer de fichier
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <FileText className="h-12 w-12 text-slate-400" />
                  <div>
                    <p className="font-semibold text-slate-700">
                      Cliquez pour sélectionner un fichier Word
                    </p>
                    <p className="text-sm text-slate-500">
                      Format supporté: .docx (Word 2007+)
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Erreur */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Indicateur de conversion */}
            {converting && (
              <div className="flex items-center justify-center gap-3 p-4 bg-purple-50 rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                <span className="text-purple-800">Conversion en cours...</span>
              </div>
            )}

            {/* Prévisualisation du HTML converti */}
            {convertedHtml && !converting && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-bold">Contenu converti</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewMode(!previewMode)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    {previewMode ? 'Voir HTML' : 'Voir aperçu'}
                  </Button>
                </div>

                <div className="border rounded-lg bg-white max-h-[400px] overflow-y-auto">
                  {previewMode ? (
                    <div 
                      className="p-4 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(convertedHtml) }}
                    />
                  ) : (
                    <pre className="p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono">
                      {convertedHtml}
                    </pre>
                  )}
                </div>

                <p className="text-xs text-slate-500">
                  💡 Le contenu sera utilisé pour le corps de la convention. 
                  Vous pourrez le modifier après import.
                </p>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
              <h4 className="font-semibold mb-2">📋 Conseils pour un bon import :</h4>
              <ul className="list-disc ml-5 space-y-1">
                <li>Utilisez les styles "Titre 1", "Titre 2", etc. pour les en-têtes</li>
                <li>Le texte en gras et italique sera conservé</li>
                <li>Les listes à puces et numérotées sont supportées</li>
                <li>Les tableaux simples sont convertis</li>
                <li>Les images ne sont pas importées (uniquement le texte)</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button
              onClick={handleImport}
              disabled={!convertedHtml || converting}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Utiliser ce contenu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
